import React, { useMemo, useState } from 'react'
import { Box, Button, OutlinedInput } from '@mui/material'
import {
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import { nodeTypesAtom, replacementsAtom } from './atoms'
import { Table } from 'shared/ui/Table/Table'
import { ReplacementData } from './types'
import { NodeTypeSelector } from '../explore/NodeTypeSelector'
import { GoogleLoginButton } from 'infrastructure/googleDrive'
import { useGoogleSheetTransform, useReplacementsAdd } from './utility'
import { GoogleSheetImport } from '../googleApis/GoogleSheetImport'
import { Checkmark } from '../../../ingestion/IMSpanish/components/Checkmark'
import { RegexInput } from '../../utility/RegexInput'

export function ReplacementInputs() {
  return (
    <Box>
      <Box padding="10px">
        <ReplaceInput />
      </Box>
      <ClearReplacementsButton />
      <CurrentInputs />
    </Box>
  )
}

function CurrentInputs() {
  const replacements = useAtomValue(replacementsAtom)
  const columns = useCurrentInputsColumns()
  return (
    <Box>
      <Table overrideHeight={240} columns={columns} items={replacements} />
    </Box>
  )
}

function ClearReplacementsButton() {
  const setReplacements = useSetAtom(replacementsAtom)
  return <Button onClick={() => setReplacements([])}>Clear All</Button>
}

function ReplaceInput() {
  const [caseSensitive, setCaseSensitive] = useState(true)
  const [types, setTypes] = useAtom(nodeTypesAtom)
  const [asRegex, setAsRegex] = useState<RegExp | string>('')
  const [replaceWith, setReplaceWith] = useState('')
  const addReplacement = useReplacementsAdd()

  const doAdd = () => {
    addReplacement({ existing: asRegex, replaceWith, caseSensitive })
    setReplaceWith('')
  }

  return (
    <Box display="flex" flexDirection="row" alignItems="center">
      <RegexInput
        asRegex={asRegex}
        setAsRegex={setAsRegex}
        caseSensitive={caseSensitive}
        setCaseSensitive={setCaseSensitive}
      />
      <OutlinedInput
        placeholder="Replace With"
        value={replaceWith}
        onChange={({ target: { value } }) => setReplaceWith(value)}
      />
      <Button disabled={!asRegex} onClick={doAdd}>
        Add
      </Button>
      <NodeTypeSelector selectionChanged={setTypes} selectedTypes={types} />
      <Box padding="10px">
        <GoogleLoginButton
          hideWhenLoggedIn={true}
          LoggedInComponent={ReplacementsFromGoogleDoc}
        />
      </Box>
    </Box>
  )
}

function ReplacementsFromGoogleDoc() {
  const transposeFunc = useGoogleSheetTransform()
  return <GoogleSheetImport onRetrieveData={transposeFunc} />
}

function useCurrentInputsColumns() {
  const replacementsSetter = useSetAtom(replacementsAtom)
  return useMemo(() => {
    return [
      {
        title: <strong>Searching For</strong>,
        width: 100,
        rowContent({ existing, replaceWith }: ReplacementData) {
          if (existing === replaceWith) {
            return <span style={{ color: 'red' }}>{String(existing)}</span>
          }
          return String(existing)
        },
      },
      {
        title: <strong>Case Sensitive</strong>,
        width: 50,
        rowContent({ existing }: ReplacementData) {
          const caseInsensitive =
            existing instanceof RegExp && existing.flags.includes('i')
          return caseInsensitive ? null : <Checkmark />
        },
      },
      {
        title: <strong>Replacing With</strong>,
        width: 100,
        rowContent({ replaceWith, existing }: ReplacementData) {
          if (existing === replaceWith) {
            return <span style={{ color: 'red' }}>{String(replaceWith)}</span>
          }
          return replaceWith
        },
      },
      {
        title: <strong>Cancel</strong>,
        width: 50,
        rowContent(data: ReplacementData) {
          const onClick = () => {
            replacementsSetter((prev) => prev.filter((item) => item !== data))
          }
          return <Button onClick={onClick}>X</Button>
        },
      },
    ]
  }, [])
}
